import { useId, useMemo, useState } from "react";
import type { FormEvent, MouseEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  api,
  FINDING_STATUSES,
  SEVERITIES,
  unwrap,
  type FindingItem,
  type FindingStatus,
  type FindingsQuery,
  type Severity,
  type StatusOut,
} from "../../api/client";
import { StatusEditor } from "../../components/StatusEditor";
import { ErrorMessage, Loading, Pagination, SeverityBadge } from "../../components/ui";
import { t, type MessageKey } from "../../i18n/t";
import { formatNumber, formatSpeed, severityLabel, statusLabel } from "../../lib/format";
import { routes } from "../../lib/routes";
import { usePageTitle } from "../../lib/usePageTitle";
import { useQuery } from "../../lib/useQuery";
import { useKommune } from "./KommuneContext";

export const PAGE_SIZE = 50;
/** Largest page the API allows; used once to collect filter options (criteria, schools). */
const FACET_PAGE_SIZE = 500;

type Sort = NonNullable<FindingsQuery["sort"]>;
const SORTS: { value: Sort; label: MessageKey }[] = [
  { value: "rank", label: "tiltak.sortRank" },
  { value: "exposure", label: "tiltak.sortExposure" },
  { value: "severity", label: "tiltak.sortSeverity" },
  { value: "status", label: "tiltak.sortStatus" },
];

const SURROUNDINGS: { value: NonNullable<FindingsQuery["omgivelse"]>; label: MessageKey }[] = [
  { value: "boliger", label: "tiltak.surroundingsBoliger" },
  { value: "faa_boliger", label: "tiltak.surroundingsFaaBoliger" },
];

/** URL search params (Norwegian, shareable) → API query. */
function queryFromParams(params: URLSearchParams): FindingsQuery {
  const severity = params
    .getAll("alvorlighet")
    .filter((s): s is Severity => (SEVERITIES as readonly string[]).includes(s));
  const status = params
    .getAll("status")
    .filter((s): s is FindingStatus => (FINDING_STATUSES as readonly string[]).includes(s));
  const sortParam = params.get("sortering");
  const sort = SORTS.find((s) => s.value === sortParam)?.value ?? "rank";
  const page = Math.max(1, Number.parseInt(params.get("side") ?? "1", 10) || 1);
  const query: FindingsQuery = { sort, page, page_size: PAGE_SIZE };
  const school = params.get("skole");
  const criterion = params.get("kriterium");
  const q = params.get("q")?.trim();
  if (school) query.school = school;
  if (severity.length) query.severity = severity;
  if (status.length) query.status = status;
  if (criterion) query.criterion = criterion;
  const omgivelse = SURROUNDINGS.find((o) => o.value === params.get("omgivelse"))?.value;
  if (omgivelse) query.omgivelse = omgivelse;
  if (q) query.q = q;
  return query;
}

function isInteractive(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest("a, button, input, select, textarea, label");
}

export function TiltakPage() {
  const { orgId, nr, navn, overview, reloadOverview } = useKommune();
  const [urlParams, setUrlParams] = useSearchParams();
  // React Router applies URL updates in a transition; keep the requested params locally until the
  // URL catches up so the controlled filter inputs respond immediately.
  const [pending, setPending] = useState<URLSearchParams | null>(null);
  if (pending && pending.toString() === urlParams.toString()) setPending(null);
  const params = pending ?? urlParams;
  const setParams = (next: URLSearchParams) => {
    setPending(next);
    setUrlParams(next);
  };
  const navigate = useNavigate();
  const query = useMemo(() => queryFromParams(params), [params]);
  const formId = useId();
  usePageTitle(`${t("tiltak.title")} – ${navn}`);

  const list = useQuery(
    `findings:${orgId}:${nr}:${JSON.stringify(query)}`,
    (signal) =>
      unwrap(
        api.GET("/api/orgs/{org_id}/kommuner/{nummer}/findings", {
          params: { path: { org_id: orgId, nummer: nr }, query },
          signal,
        }),
      ),
    { keepPrevious: true },
  );

  const facets = useQuery(`facets:${orgId}:${nr}`, (signal) =>
    unwrap(
      api.GET("/api/orgs/{org_id}/kommuner/{nummer}/findings", {
        params: { path: { org_id: orgId, nummer: nr }, query: { page_size: FACET_PAGE_SIZE } },
        signal,
      }),
    ),
  );

  const criteria = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of facets.data?.items ?? [])
      if (!m.has(f.criterion_id)) m.set(f.criterion_id, f.title);
    if (query.criterion && !m.has(query.criterion)) m.set(query.criterion, query.criterion);
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [facets.data, query.criterion]);

  const schools = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of overview?.schools ?? []) m.set(s.school_id, s.name);
    for (const f of facets.data?.items ?? [])
      f.school_ids.forEach((id, i) => m.has(id) || m.set(id, f.school_names[i] ?? id));
    if (query.school && !m.has(query.school)) m.set(query.school, query.school);
    return [...m.entries()].sort(([, a], [, b]) => a.localeCompare(b, "nb"));
  }, [overview, facets.data, query.school]);

  const [qDraft, setQDraft] = useState(query.q ?? "");
  const [qFromUrl, setQFromUrl] = useState(query.q ?? "");
  if ((query.q ?? "") !== qFromUrl) {
    setQFromUrl(query.q ?? "");
    setQDraft(query.q ?? "");
  }

  function update(mutate: (p: URLSearchParams) => void) {
    const next = new URLSearchParams(params);
    mutate(next);
    next.delete("side"); // a new filter starts on the first page
    setParams(next);
  }

  function setSingle(name: string, value: string) {
    update((p) => (value ? p.set(name, value) : p.delete(name)));
  }

  function toggleMulti(name: string, value: string, on: boolean) {
    update((p) => {
      const values = p.getAll(name).filter((v) => v !== value);
      if (on) values.push(value);
      p.delete(name);
      values.forEach((v) => p.append(name, v));
    });
  }

  function onSearch(e: FormEvent) {
    e.preventDefault();
    setSingle("q", qDraft.trim());
  }

  function onPage(page: number) {
    const next = new URLSearchParams(params);
    if (page > 1) next.set("side", String(page));
    else next.delete("side");
    setParams(next);
  }

  function onSaved(key: string, out: StatusOut) {
    list.setData(
      (prev) =>
        prev && {
          ...prev,
          items: prev.items.map((i) =>
            i.key === key
              ? {
                  ...i,
                  status: out.status,
                  comment: out.comment,
                  status_updated_at: out.updated_at,
                }
              : i,
          ),
        },
    );
    reloadOverview();
  }

  function mapUrl(f: FindingItem): string | null {
    const school =
      (query.school && f.school_ids.includes(query.school) ? query.school : null) ??
      f.school_ids[0];
    return school ? routes.school(orgId, nr, school, f.key) : null;
  }

  function onRowClick(e: MouseEvent<HTMLTableRowElement>, url: string | null) {
    if (!url || isInteractive(e.target)) return;
    navigate(url);
  }

  const data = list.data;
  const hasFilters = [
    "skole",
    "alvorlighet",
    "status",
    "kriterium",
    "omgivelse",
    "q",
    "sortering",
  ].some((k) => params.has(k));

  return (
    <div className="page">
      <header className="page-header">
        <p className="eyebrow">{navn}</p>
        <h1>{t("tiltak.title")}</h1>
        <p className="muted">{t("tiltak.intro")}</p>
      </header>

      <form className="filters" id={formId} onSubmit={onSearch} aria-label={t("tiltak.filters")}>
        <div className="field">
          <label htmlFor={`${formId}-school`}>{t("tiltak.school")}</label>
          <select
            id={`${formId}-school`}
            value={query.school ?? ""}
            onChange={(e) => setSingle("skole", e.target.value)}
          >
            <option value="">{t("tiltak.allSchools")}</option>
            {schools.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <fieldset className="field checkbox-group">
          <legend>{t("tiltak.severity")}</legend>
          {SEVERITIES.map((s) => (
            <label key={s} className="checkbox">
              <input
                type="checkbox"
                checked={query.severity?.includes(s) ?? false}
                onChange={(e) => toggleMulti("alvorlighet", s, e.target.checked)}
              />
              {severityLabel(s)}
            </label>
          ))}
        </fieldset>
        <fieldset className="field checkbox-group">
          <legend>{t("tiltak.status")}</legend>
          {FINDING_STATUSES.map((s) => (
            <label key={s} className="checkbox">
              <input
                type="checkbox"
                checked={query.status?.includes(s) ?? false}
                onChange={(e) => toggleMulti("status", s, e.target.checked)}
              />
              {statusLabel(s)}
            </label>
          ))}
        </fieldset>
        <div className="field">
          <label htmlFor={`${formId}-criterion`}>{t("tiltak.criterion")}</label>
          <select
            id={`${formId}-criterion`}
            value={query.criterion ?? ""}
            onChange={(e) => setSingle("kriterium", e.target.value)}
          >
            <option value="">{t("tiltak.allCriteria")}</option>
            {criteria.map(([id, title]) => (
              <option key={id} value={id}>
                {title}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`${formId}-surroundings`}>{t("tiltak.surroundings")}</label>
          <select
            id={`${formId}-surroundings`}
            value={query.omgivelse ?? ""}
            onChange={(e) => setSingle("omgivelse", e.target.value)}
          >
            <option value="">{t("tiltak.allSurroundings")}</option>
            {SURROUNDINGS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(o.label)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`${formId}-sort`}>{t("tiltak.sort")}</label>
          <select
            id={`${formId}-sort`}
            value={query.sort ?? "rank"}
            onChange={(e) =>
              setSingle("sortering", e.target.value === "rank" ? "" : e.target.value)
            }
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {t(s.label)}
              </option>
            ))}
          </select>
        </div>
        <div className="field search-field">
          <label htmlFor={`${formId}-q`}>{t("tiltak.search")}</label>
          <div className="input-row">
            <input
              id={`${formId}-q`}
              type="search"
              value={qDraft}
              maxLength={100}
              onChange={(e) => setQDraft(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              {t("tiltak.searchButton")}
            </button>
          </div>
        </div>
        {hasFilters && (
          <div className="field filters-reset">
            <button
              type="button"
              className="btn btn-link"
              onClick={() => setParams(new URLSearchParams())}
            >
              {t("tiltak.reset")}
            </button>
          </div>
        )}
      </form>

      {list.error && <ErrorMessage error={list.error} onRetry={list.reload} />}
      {!data && !list.error && <Loading />}
      {data && (
        <section className="section" aria-busy={list.loading}>
          <p className="result-count" role="status" aria-live="polite">
            {t("tiltak.caption", { total: formatNumber(data.total) })}
          </p>
          {data.items.length === 0 ? (
            <p>{t("tiltak.empty")}</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table tiltak-table">
                <caption className="visually-hidden">
                  {t("tiltak.caption", { total: formatNumber(data.total) })}
                </caption>
                <thead>
                  <tr>
                    <th scope="col" className="num">
                      {t("tiltak.colRank")}
                    </th>
                    <th scope="col">{t("tiltak.colSeverity")}</th>
                    <th scope="col">{t("tiltak.colTitle")}</th>
                    <th scope="col">{t("tiltak.colLocation")}</th>
                    <th scope="col">{t("tiltak.colSchools")}</th>
                    <th scope="col" className="num" title={t("tiltak.exposureTitle")}>
                      {t("tiltak.colExposure")}
                    </th>
                    <th scope="col" className="num">
                      {t("tiltak.colAadt")}
                    </th>
                    <th scope="col" className="num">
                      {t("tiltak.colSpeed")}
                    </th>
                    <th scope="col" className="num" title={t("tiltak.fauTitle")}>
                      {t("tiltak.colFau")}
                    </th>
                    <th scope="col">{t("tiltak.colStatus")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((f) => {
                    const url = mapUrl(f);
                    return (
                      <tr
                        key={f.key}
                        className={url ? "clickable" : undefined}
                        onClick={(e) => onRowClick(e, url)}
                      >
                        <td className="num">{f.rank}</td>
                        <td>
                          <SeverityBadge severity={f.severity} />
                        </td>
                        <th scope="row" className="title-cell">
                          {url ? <Link to={url}>{f.title}</Link> : f.title}
                        </th>
                        <td>{f.location}</td>
                        <td>{f.school_names.join(", ")}</td>
                        <td className="num">{formatNumber(f.exposure)}</td>
                        <td className="num">{formatNumber(f.aadt)}</td>
                        <td className="num nowrap">{formatSpeed(f.speed)}</td>
                        <td className="num">{formatNumber(f.fau_confirmations)}</td>
                        <td>
                          <StatusEditor
                            key={f.key}
                            variant="inline"
                            orgId={orgId}
                            nr={nr}
                            findingKey={f.key}
                            title={`${f.title}, ${f.location}`}
                            status={f.status}
                            comment={f.comment}
                            updatedAt={f.status_updated_at}
                            onSaved={(out) => onSaved(f.key, out)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <Pagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            onPage={onPage}
          />
        </section>
      )}
    </div>
  );
}
